import { CustomerTypes } from "@faire/help-center-utils/enums/CustomerType";
import { getCustomerType } from "@faire/help-center-utils/lib/customer";
import { localeString } from "@faire/help-center-utils/lib/localeString";
import { isLoggedInBrand } from "@faire/help-center-utils/serialized-data/getBrand";
import { getSettingHelpCenterSearchAndNavImprovementsWithoutAssignment } from "@faire/help-center-utils/settings/getSettingHelpCenterSearchAndNavImprovements";
import { useQueryParams } from "@faire/web--source/ui/hooks/useQueryParams";
import { useQuery as useZendeskArticles } from "@faire/web-api--source/api/zendesk/locale/articles/articleId/get-hooks";
import { useQuery as useZendeskSections } from "@faire/web-api--source/api/zendesk/locale/sections/get-hooks";
import { IZendeskSection } from "@faire/web-api--source/com/faire/vendor/zendesk/IZendeskSection";
import { route as homeRoute } from "@faire/web-api--source/routes/support";
import { route as brandsRoute } from "@faire/web-api--source/routes/support/brands";
import { route as retailersRoute } from "@faire/web-api--source/routes/support/retailers";
import {
  QueryParameters as SearchQueryParams,
  route as searchRoute,
} from "@faire/web-api--source/routes/support/search";
import { useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";

import { SupportParams } from "../consts";
import { useArticleContextStore } from "../views/articles/hooks/ArticleContext";
import { useArticleId } from "../views/articles/hooks/useArticleId";

export const useCustomerType = () => {
  const { categoryId: categoryIdParam } = useParams<SupportParams>();
  const { categoryId: categoryIdQueryParam } = useQueryParams(
    SearchQueryParams.parse
  );
  const articleId = useArticleId();
  const [{ articleData }] = useArticleContextStore(["articleData"]);
  const { data: zendeskArticleResponse } = useZendeskArticles(
    localeString,
    articleId ?? "",
    { include: undefined },
    {
      enabled:
        !!articleId &&
        articleData &&
        articleData.article &&
        articleId !== String(articleData.article.id),
    }
  );
  const location = useLocation();
  const shouldFetchSections =
    location.pathname !== homeRoute &&
    location.pathname !== brandsRoute &&
    location.pathname !== retailersRoute &&
    location.pathname !== searchRoute;
  const { data: sectionsResponse } = useZendeskSections(localeString, {
    enabled: shouldFetchSections,
  });
  const articleSections = zendeskArticleResponse?.sections;
  const articleSection = articleSections?.[0];
  const { topicId } = useParams<SupportParams>();
  const sectionId = topicId ?? zendeskArticleResponse?.article?.section_id;

  const sections: IZendeskSection[] = articleSections?.length
    ? articleSections
    : sectionsResponse?.sections ?? [];

  const currentSection = sections.filter(
    (section) => section.id === Number(sectionId)
  );

  const categoryId =
    categoryIdParam ??
    categoryIdQueryParam ??
    currentSection?.[0]?.category_id ??
    articleSection?.category_id;

  const customerType = useMemo(() => {
    if (location.pathname === brandsRoute) {
      return CustomerTypes.BRAND;
    } else if (location.pathname === retailersRoute) {
      return CustomerTypes.RETAILER;
    } else if (
      location.pathname === homeRoute &&
      getSettingHelpCenterSearchAndNavImprovementsWithoutAssignment()
    ) {
      if (isLoggedInBrand()) {
        return CustomerTypes.BRAND;
      }
      return CustomerTypes.RETAILER;
    } else {
      return getCustomerType(Number(categoryId));
    }
  }, [location.pathname, categoryId]);
  return {
    customerType,
  };
};
